/* CardcLub mandatory storefront account gate, task 13/17.
 * Drop-in replacement; load once, before purchase handlers when possible.
 * Liquid's customer flag controls UI only. Enforce identity on the server too.
 * This asset never sends cart/checkout requests, collects passwords, or creates accounts.
 */
(() => {
  'use strict';
  if (window.CardcAccountGate) return;

  const root = document.documentElement;
  const KEY = 'ccAccountGatePending:v1';
  const TTL = 30 * 60 * 1000;
  const MAX_RECORD = 24000;
  const NOTICE = 'cc-account-gate-notice';
  const DYNAMIC = '.shopify-payment-button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart, shopify-buy-it-now-button, [data-shopify="payment-button"]';
  const FIELD = /^(?:id|quantity|selling_plan|properties\[[^\]\x00-\x1f]{1,120}\]|options\[[^\]\x00-\x1f]{1,120}\])$/;
  const NUMBER_ID = /^\d{1,30}$/;
  const masked = new Map();
  const replacements = new WeakMap();
  const restored = new WeakMap();
  const dispatchedHere = new Set();
  let memory = null;
  let storageFailed = false;
  let noticeKey = '';
  let dismissedKey = '';
  let focusBack = null;
  let scheduled = false;

  const copy = {
    required: ['Sign in or create your store account before adding items or checking out.', '请先登录或注册店铺账户，再加入购物车或结算。', 'Log in of maak een winkelaccount aan voordat je artikelen toevoegt of afrekent.'],
    otp: ['Use your email and Shopify’s one-time code. Use the same email as your existing account.', '使用邮箱和 Shopify 一次性验证码；已有账户请使用原邮箱。', 'Gebruik je e-mailadres en de eenmalige Shopify-code. Gebruik voor een bestaand account hetzelfde e-mailadres.'],
    login: ['Sign in or create an account', '登录 / 注册账户', 'Inloggen of een account aanmaken'],
    close: ['Continue browsing', '继续浏览', 'Verder winkelen'],
    saved: ['Your selection is saved in this tab for 30 minutes. After signing in, review it before continuing.', '已在本标签页保存选择 30 分钟；登录后请核对再继续。', 'Je selectie blijft 30 minuten in dit tabblad bewaard. Controleer deze na het inloggen.'],
    storage: ['This browser could not save your selection for a page change. Sign in, then review your selection again.', '当前浏览器无法跨页面保存选择；登录后请重新核对商品选择。', 'Deze browser kon je selectie niet bewaren voor een paginawissel. Log in en controleer je selectie opnieuw.'],
    ready: ['You are signed in. Your previous selection is restored; review it, then add it once.', '已登录并恢复先前选择；请核对后加入购物车。', 'Je bent ingelogd. Je vorige selectie is hersteld; controleer deze en voeg de artikelen toe.'],
    resume: ['Add saved selection to cart', '将已保存的选择加入购物车', 'Opgeslagen selectie toevoegen'],
    manual: ['You are signed in. Review your selection and use the original Buy now, checkout, or choose-options control to continue.', '已登录。请核对选择，再使用原来的立即购买、结算或选款按钮继续。', 'Je bent ingelogd. Controleer je selectie en gebruik de oorspronkelijke knop om verder te gaan.'],
    missing: ['Your selection is still saved. Reopen the original product or quick-add options to review it; nothing was added automatically.', '选择仍已保存。请重新打开原商品或快捷选款进行核对；尚未自动加购。', 'Je selectie is nog bewaard. Open het oorspronkelijke product of de opties opnieuw om deze te controleren.'],
    failed: ['Your selection is still saved. Review the product form and use its Add to cart button to retry.', '选择仍已保存。请核对商品表单，再用原加购按钮重试。', 'Je selectie is nog bewaard. Controleer het productformulier en probeer opnieuw met de oorspronkelijke knop.'],
    dispatched: ['Your previous selection is restored. Check your cart and any product error before retrying; this page will not add it again automatically.', '已恢复上次选择。重试前请检查购物车和商品错误提示；本页不会自动重复加购。', 'Je vorige selectie is hersteld. Controleer je winkelwagen en eventuele foutmelding voordat je opnieuw probeert. Deze pagina voegt niets automatisch opnieuw toe.'],
    buy: ['Sign in to buy now', '登录后立即购买', 'Inloggen om direct te kopen'],
    checkout: ['Sign in to check out', '登录后结算', 'Inloggen om af te rekenen'],
    title: ['Store account', '店铺账户', 'Winkelaccount']
  };
  function t(key) {
    const lang = (root.lang || 'en').toLowerCase();
    return window.CardclubCustomUI?.t('gate.' + key) || copy[key][lang.startsWith('zh') ? 1 : lang.startsWith('nl') ? 2 : 0];
  }
  // Only the existing server-rendered customer flag controls the gate. Selection
  // storage, return URLs and successful navigation are never proof of identity.
  function signedIn() { return root.dataset.ccProfileComplete === 'true'; }
  function localURL(value) {
    if (typeof value !== 'string' || !value || value.length > 2048 || value !== value.trim()) return null;
    if (/[\\\x00-\x20\x7f]/.test(value) || value.startsWith('//')) return null;
    if (!value.startsWith('/') && !/^https?:\/\//i.test(value)) return null;
    try {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || url.username || url.password) return null;
      if (/%(?:2f|5c|25|0[0-9a-f]|1[0-9a-f]|7f)/i.test(url.pathname)) return null;
      decodeURIComponent(url.pathname); // Reject malformed percent escapes.
      return url;
    } catch (_) { return null; }
  }
  function barePath(path) { return path.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/)/i, '') || '/'; }
  function storeRoot() {
    const url = localURL(root.dataset.ccStoreRoot || '/');
    return url && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?$/i.test(url.pathname) ? url.pathname : '/';
  }
  function safeReturn(value) {
    const url = localURL(value);
    if (!url) return storeRoot();
    const path = barePath(url.pathname);
    // Only non-mutating storefront destinations; no login loops or redirect endpoints.
    if (!(path === '/' || /^\/(?:products|collections|blogs|pages)(?:\/|$)/.test(path) ||
          path === '/search' || /^\/apps\/cardclub-bundles(?:\/cart)?\/?$/.test(path))) return storeRoot();
    if (/^\/pages\/join-cardclub(?:\/|$)/.test(path)) return storeRoot();
    const clean = new URL(url.pathname, location.origin);
    // Never put personalization, email, tokens, or nested redirect parameters in a return URL.
    const rules = { variant: NUMBER_ID, country: /^[A-Z]{2}$/, currency: /^[A-Z]{3}$/, locale: /^[a-z]{2}(?:-[a-z]{2})?$/i, preview_theme_id: NUMBER_ID };
    for (const [name, rule] of Object.entries(rules)) {
      const values = url.searchParams.getAll(name);
      if (values.length === 1 && rule.test(values[0])) clean.searchParams.set(name, values[0]);
    }
    return clean.pathname + clean.search;
  }
  function loginURL(returnTo) {
    // New customer accounts use one OTP entry for both login and account creation.
    // ccRegistrationUrl is deliberately not used as a legacy-password signup route.
    let url = localURL(root.dataset.ccLoginUrl || window.routes?.account_login_url || '');
    if (!url || !/^\/(?:account\/login|customer_authentication\/login)\/?$/.test(barePath(url.pathname))) {
      url = new URL(storeRoot() + 'account/login', location.origin);
    }
    const clean = new URL(url.pathname, location.origin);
    for (const [name, rule] of [['locale', /^[a-z]{2}(?:-[a-z]{2})?$/i], ['region_country', /^[A-Z]{2}$/], ['ui_hint', /^(?:full|minimal)$/]]) {
      if (url.searchParams.getAll(name).length === 1 && rule.test(url.searchParams.get(name))) clean.searchParams.set(name, url.searchParams.get(name));
    }
    clean.searchParams.set('return_to', safeReturn(returnTo));
    return clean.pathname + clean.search;
  }
  function purchaseForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    const action = localURL(form.getAttribute('action') || '');
    return !!action && /^\/cart\/add(?:\.js|\.json)?\/?$/.test(barePath(action.pathname));
  }
  function checkoutURL(value) {
    const url = localURL(value);
    return !!url && (/^\/checkouts?(?:\/|$)/.test(barePath(url.pathname)) ||
      /^\/cart\/\d+:\d+/.test(barePath(url.pathname)) ||
      (barePath(url.pathname) === '/cart' && url.searchParams.has('checkout')));
  }
  function elements(event) { return (event.composedPath?.() || [event.target]).filter(node => node instanceof Element); }
  function associatedForm(node) {
    return node?.form || node?.closest('form') ||
      node?.closest('product-form-component, product-form')?.querySelector('form') || null;
  }
  function fieldsFrom(form) {
    if (!purchaseForm(form)) return [];
    try {
      const fields = [];
      for (const [name, value] of new FormData(form)) {
        if (!FIELD.test(name)) continue;
        // File inputs cannot survive navigation; never silently resume without the file.
        if (typeof value !== 'string' || value.length > 2000 || fields.length >= 64) return [];
        fields.push([name, value]);
      }
      const ids = fields.filter(([name]) => name === 'id');
      if (ids.length !== 1 || !NUMBER_ID.test(ids[0][1])) return [];
      if (!fields.some(([name]) => name === 'quantity')) {
        fields.push(['quantity', form.closest('product-form-component, product-form')?.dataset.quantityDefault || '1']);
      }
      return validFields(fields) ? fields : [];
    } catch (_) { return []; }
  }
  function validFields(fields) {
    if (!Array.isArray(fields) || fields.length > 64) return false;
    if (!fields.every(pair => Array.isArray(pair) && pair.length === 2 && typeof pair[0] === 'string' && FIELD.test(pair[0]) && typeof pair[1] === 'string' && pair[1].length <= 2000)) return false;
    for (const name of ['id', 'quantity', 'selling_plan']) {
      const values = fields.filter(([key]) => key === name).map(([, value]) => value);
      if (values.length > 1) return false;
      if (name === 'id' && (values.length !== 1 || !NUMBER_ID.test(values[0]))) return false;
      if (name === 'quantity' && (values.length !== 1 || !/^[1-9]\d{0,4}$/.test(values[0]))) return false;
      if (name === 'selling_plan' && values[0] && !NUMBER_ID.test(values[0])) return false;
    }
    return true;
  }
  function write(record) {
    memory = record;
    try {
      const raw = JSON.stringify(record);
      if (raw.length > MAX_RECORD) throw new Error('selection-too-large');
      sessionStorage.setItem(KEY, raw);
      storageFailed = false;
      return true;
    } catch (_) {
      storageFailed = true;
      // Do not leave an older selection on disk if the replacement failed to save.
      try { sessionStorage.removeItem(KEY); } catch (_) { /* Tab memory still preserves the selection. */ }
      return false;
    }
  }
  function forget() {
    memory = null;
    try { sessionStorage.removeItem(KEY); } catch (_) { /* Storage may be disabled. */ }
  }
  function read() {
    let record = memory;
    try {
      if (!record) {
        const raw = sessionStorage.getItem(KEY);
        if (raw && raw.length <= MAX_RECORD) {
          try { record = JSON.parse(raw); } catch (_) { forget(); return null; }
        }
        else if (raw) forget();
      }
    } catch (_) { storageFailed = true; }
    if (!record) return null;
    const now = Date.now();
    if (record.version !== 1 || typeof record.id !== 'string' || record.id.length > 80 ||
        !Number.isFinite(record.createdAt) || record.createdAt > now || now - record.createdAt >= TTL ||
        !['pending', 'dispatched'].includes(record.state) || !['add', 'buy', 'choose', 'checkout'].includes(record.intent) ||
        typeof record.formId !== 'string' || record.formId.length > 200 ||
        typeof record.returnTo !== 'string' || safeReturn(record.returnTo) !== record.returnTo ||
        !(Array.isArray(record.fields) && (record.fields.length === 0 || validFields(record.fields)))) {
      forget(); return null;
    }
    return record;
  }
  function selectionReturn(returnTo, fields) {
    const destination = new URL(safeReturn(returnTo), location.origin);
    // Ask the native product render to restore the entire selected variant (price,
    // image and picker), rather than changing just a hidden variant input later.
    if (/^\/products\//.test(barePath(destination.pathname)) && fields.length) {
      destination.searchParams.set('variant', fields.find(([name]) => name === 'id')[1]);
    }
    return safeReturn(destination.pathname + destination.search);
  }
  function remember(form, intent, returnTo) {
    const fields = fieldsFrom(form);
    const record = {
      version: 1,
      id: window.crypto?.randomUUID?.() || String(Date.now()) + '-' + Math.random().toString(36).slice(2),
      createdAt: Date.now(), state: 'pending', intent,
      returnTo: selectionReturn(returnTo || location.pathname + location.search, fields),
      formId: purchaseForm(form) ? (form.getAttribute('id') || '').slice(0, 200) : '',
      fields
    };
    write(record);
    return record;
  }
  function focus(node) { try { node?.focus({ preventScroll: true }); } catch (_) { /* A route link remains available. */ } }
  function notice(key, message, mode, record) {
    if (!document.body) return null;
    if (dismissedKey === key) return null;
    let box = document.getElementById(NOTICE);
    if (box && noticeKey === key) return box;
    box?.remove();
    noticeKey = key;
    box = document.createElement('section');
    box.id = NOTICE;
    box.tabIndex = -1;
    box.setAttribute('role', 'region');
    box.setAttribute('aria-labelledby', NOTICE + '-title');
    box.style.cssText = 'position:fixed;inset:auto 1rem 1rem;z-index:2147483000;max-inline-size:38rem;margin-inline:auto;padding:1rem;border:1px solid #b8c4d2;border-radius:12px;background:#14244a;color:#fff;box-shadow:0 8px 30px #0004;font:15px/1.5 system-ui;max-block-size:75vh;overflow:auto';
    const heading = document.createElement('h2');
    heading.id = NOTICE + '-title';
    heading.textContent = t('title');
    heading.style.cssText = 'font:inherit;font-weight:700;margin:0 0 .5rem';
    const status = document.createElement('p');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.textContent = message;
    status.style.margin = '0 0 .75rem';
    box.append(heading, status);
    function control(node) {
      node.style.cssText = 'display:inline-block;min-block-size:44px;margin:.25rem;padding:.5rem .75rem;border:1px solid currentColor;border-radius:6px;font:inherit;cursor:pointer;background:#fff;color:#14244a;text-decoration:underline';
      box.appendChild(node);
      return node;
    }
    if (mode === 'login') {
      const explanation = document.createElement('p');
      explanation.textContent = t('otp') + ' ' + t(storageFailed ? 'storage' : 'saved');
      box.appendChild(explanation);
      const link = control(document.createElement('a'));
      link.href = loginURL(record?.returnTo || location.pathname + location.search);
      link.textContent = t('login'); link.dataset.ccAccountLogin = '';
      link.addEventListener('click', () => {
        const current = read();
        if (!current || current.id !== record?.id || current.state !== 'pending') return;
        // Capture edits made while the non-modal notice was visible, immediately
        // before handing off to native sign-in. This never submits a product form.
        const fields = fieldsFrom(matchingForm(current));
        if (fields.length) {
          current.fields = fields;
          current.returnTo = selectionReturn(current.returnTo, fields);
          write(current);
        }
        link.href = loginURL(current.returnTo);
        // Back after a canceled login must ask the server for the same variant
        // even if the native picker's asynchronous URL update had not finished.
        // Keep existing attribution/query/hash and history state unchanged.
        if (fields.length && /^\/products\//.test(barePath(location.pathname))) {
          try {
            const back = new URL(location.href);
            back.searchParams.set('variant', fields.find(([name]) => name === 'id')[1]);
            history.replaceState(history.state, '', back.pathname + back.search + back.hash);
          } catch (_) { /* The login return URL still requests the selected variant. */ }
        }
      });
    } else if (mode === 'resume') {
      const button = control(document.createElement('button'));
      button.type = 'button'; button.textContent = t('resume'); button.dataset.ccAccountResume = '';
      button.addEventListener('click', () => resumePending());
    }
    const close = control(document.createElement('button'));
    close.type = 'button'; close.textContent = t('close');
    close.addEventListener('click', () => {
      dismissedKey = key; box.remove(); noticeKey = '';
      if (focusBack?.isConnected) focus(focusBack);
    });
    document.body.appendChild(box);
    return box;
  }
  function requireAccount(options = {}) {
    if (signedIn()) return true;
    focusBack = options.invoker || document.activeElement;
    const record = remember(options.form, options.intent || 'choose', options.returnTo);
    // Close only the purchase dialog containing this action, after capturing its fields.
    // Keep the single login route usable outside an inert quick-add dialog.
    const purchaseDialog = options.form?.closest('dialog') || options.invoker?.closest('dialog');
    if (purchaseDialog?.open && typeof purchaseDialog.close === 'function') {
      try { purchaseDialog.close(); } catch (_) { /* Its own close control remains available. */ }
    }
    document.dispatchEvent(new CustomEvent('cardc:account-gate-open'));
    const box = notice('login-' + record.id, t('required'), 'login', record);
    focus(box?.querySelector('a'));
    return false;
  }
  function cancel(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
  function clickIntent(event) {
    const path = elements(event);
    // Classify the whole composed path first: a Buy now button may itself be a
    // submitter, but its intent must never become an ordinary add on return.
    const payment = path.find(node => replacements.has(node) || node.matches(DYNAMIC));
    if (payment) {
      const original = replacements.get(payment) || payment;
      return { intent: original.matches('shopify-accelerated-checkout-cart') ? 'checkout' : 'buy', form: associatedForm(original) };
    }
    for (const node of path) {
      if (node.matches('a[href]') && checkoutURL(node.getAttribute('href'))) return { intent: 'checkout', form: null };
      const form = associatedForm(node);
      if (node.matches('button, input[type="submit"], input[type="image"], [data-cc-account-required]')) {
        if (node.disabled || node.getAttribute('aria-disabled') === 'true') return null;
        if (node.matches('[name="checkout"], [data-cc-checkout]') || checkoutURL(node.getAttribute('formaction'))) return { intent: 'checkout', form };
        if (node.matches('[data-cc-account-required]')) return { intent: 'choose', form };
        const choose = node.matches('.quick-add__button--choose') || (node.getAttribute('on:click') || '').includes('quick-add-component/handleClick');
        if (choose) return { intent: 'choose', form };
        const submit = (node.tagName === 'BUTTON' && node.type === 'submit') || node.matches('input[type="submit"], input[type="image"]');
        if (purchaseForm(form) && (submit || node.getAttribute('name') === 'add' || node.closest('add-to-cart-component'))) return { intent: 'add', form };
      }
      if (node.matches('a[href]')) {
        const url = localURL(node.getAttribute('href'));
        if (url && /^\/cart\/add(?:\.js)?\/?$/.test(barePath(url.pathname))) return { intent: 'add', form: null };
      }
    }
    return null;
  }
  window.addEventListener('click', event => {
    if (signedIn()) {
      // Click-based native handlers may prevent submit entirely. Consume the saved
      // action before those handlers too, so the resume button cannot add it again.
      const action = clickIntent(event);
      if (action?.form && action.intent !== 'choose') consumePending(action.form);
      return;
    }
    const action = clickIntent(event);
    if (!action) return;
    cancel(event); // Capture before document/target handlers, including quick-add.
    action.invoker = elements(event).find(node => node.matches('button, a, input'));
    requireAccount(action);
  }, true);
  window.addEventListener('submit', event => {
    const form = elements(event).find(node => node instanceof HTMLFormElement);
    if (!form) return;
    const checkout = event.submitter?.name === 'checkout' || checkoutURL(event.submitter?.getAttribute('formaction')) || checkoutURL(form.getAttribute('action'));
    if (!purchaseForm(form) && !checkout) return;
    if (signedIn()) {
      // A shopper may choose the original native button instead of the resume button.
      // Mark our matching selection before delegation so a later reload cannot add it again.
      consumePending(form);
      return;
    }
    cancel(event);
    const buy = event.submitter?.closest(DYNAMIC);
    requireAccount({ form, intent: checkout ? 'checkout' : buy ? 'buy' : 'add', invoker: event.submitter });
  }, true);

  function consumePending(form) {
    const record = read();
    if (!record || record.state !== 'pending' || matchingForm(record) !== form) return;
    record.state = 'dispatched';
    const fields = fieldsFrom(form);
    if (fields.length) record.fields = fields;
    dispatchedHere.add(record.id); write(record);
    document.getElementById(NOTICE)?.remove(); noticeKey = '';
  }

  function matchingForm(record) {
    if (!record.fields.length || new URL(record.returnTo, location.origin).pathname !== location.pathname) return null;
    if (record.formId) {
      const form = document.getElementById(record.formId);
      return purchaseForm(form) ? form : null;
    }
    const id = record.fields.find(([name]) => name === 'id')?.[1];
    const forms = Array.from(document.forms).filter(form => purchaseForm(form) && new FormData(form).get('id') === id);
    return forms.length === 1 ? forms[0] : null; // Never choose another card's default form.
  }
  function restore(record, form) {
    if (restored.get(form) === record.id) return true;
    const id = record.fields.find(([name]) => name === 'id')?.[1];
    const variant = Array.from(form.elements).filter(el => el.name === 'id');
    // A stale/default hidden ID must not silently diverge from the native price,
    // media or visible picker. The login return URL selects the native variant.
    if (variant.some(el => el.type === 'hidden' && el.value !== id)) return false;
    const component = form.closest('product-form-component, product-form');
    const scope = form.closest('product-card, quick-add-dialog, [data-testid="product-information-details"], .shopify-section');
    if (scope && component?.dataset.productId) {
      for (const picker of scope.querySelectorAll('variant-picker')) {
        if (picker.dataset.productId !== component.dataset.productId) continue;
        const nativeVariant = picker.querySelector('script[type="application/json"]');
        if (nativeVariant) {
          try { if (String(JSON.parse(nativeVariant.textContent)?.id) !== id) return false; }
          catch (_) { return false; }
        }
      }
    }
    const groups = new Map();
    for (const [name, value] of record.fields) groups.set(name, [...(groups.get(name) || []), value]);
    const changes = [];
    for (const [name, values] of groups) {
      const controls = Array.from(form.elements).filter(el => el.name === name && !el.disabled);
      if (!controls.length) {
        if (name === 'quantity' && values[0] === '1') continue; // Shopify's absent quantity means one.
        if (name === 'selling_plan' && values[0] === '') continue;
        return false;
      }
      if (controls.some(el => el.type === 'file' || el.type === 'password')) return false;
      if (controls.every(el => el.type === 'checkbox' || el.type === 'radio')) {
        if (!values.every(value => controls.some(el => el.value === value))) return false;
        for (const el of controls) changes.push(() => { el.checked = values.includes(el.value); });
      } else if (controls.length === 1 && controls[0].tagName === 'SELECT') {
        const select = controls[0];
        if (!values.every(value => Array.from(select.options).some(option => option.value === value && !option.disabled))) return false;
        if (!select.multiple && values.length !== 1) return false;
        changes.push(() => { for (const option of select.options) option.selected = values.includes(option.value); });
      } else {
        if (controls.length !== values.length) return false;
        controls.forEach((el, i) => changes.push(() => { el.value = values[i]; }));
      }
    }
    // Do not partially overwrite a form if a property/selling plan is no longer available.
    for (const apply of changes) apply();
    restored.set(form, record.id);
    return true;
  }
  function resumePending() {
    if (!signedIn()) return false;
    const record = read();
    if (!record || record.state !== 'pending' || record.intent !== 'add') return false;
    const form = matchingForm(record);
    if (!form || !restore(record, form) || typeof form.requestSubmit !== 'function') {
      notice('missing-' + record.id, t('missing')); return false;
    }
    if (!form.reportValidity()) { notice('invalid-' + record.id, t('failed')); return false; }
    const submitters = Array.from(form.elements).filter(el =>
      ((el.tagName === 'BUTTON' && el.type === 'submit') || el.matches('input[type="submit"]')) &&
      el.name !== 'checkout' && !el.closest(DYNAMIC) &&
      (!el.hasAttribute('formaction') || purchaseFormAction(el.getAttribute('formaction'))));
    const submitter = submitters.find(el => !el.disabled);
    if (submitters.length && !submitter) { notice('disabled-' + record.id, t('failed')); return false; }
    // Record the dispatch first. AJAX handlers preventDefault on both success and failure;
    // defaultPrevented is not an acknowledgement. Keep fields for manual retry, never replay.
    record.fields = fieldsFrom(form);
    if (!record.fields.length) { notice('invalid-' + record.id, t('failed')); return false; }
    record.state = 'dispatched'; dispatchedHere.add(record.id);
    if (!write(record)) {
      // Without a durable dispatch marker, a page return could expose an older
      // pending record. Leave retries to the original control, never this replay.
      notice('failed-' + record.id, t('storage')); return false;
    }
    document.getElementById(NOTICE)?.remove(); noticeKey = '';
    try { form.requestSubmit(submitter); return true; }
    catch (_) {
      // Submission outcome is uncertain; do not reset to pending and risk a duplicate add.
      notice('failed-' + record.id, t('failed')); return false;
    }
  }
  function purchaseFormAction(value) {
    const url = localURL(value);
    return !!url && /^\/cart\/add(?:\.js|\.json)?\/?$/.test(barePath(url.pathname));
  }
  function preparePending() {
    const record = read();
    if (!record || dispatchedHere.has(record.id) || new URL(record.returnTo, location.origin).pathname !== location.pathname) return;
    const form = matchingForm(record);
    if (record.fields.length && (!form || !restore(record, form))) {
      if (signedIn()) notice('missing-' + record.id, t('missing'));
      return;
    }
    // Restoring a selection after Cancel/Back does not authenticate or purchase.
    if (!signedIn()) return;
    if (record.state === 'dispatched') {
      notice('dispatched-' + record.id, t('dispatched')); return;
    }
    const canAdd = record.intent === 'add' && !!form;
    notice((canAdd ? 'ready-' : 'manual-') + record.id, t(canAdd ? 'ready' : 'manual'), canAdd ? 'resume' : undefined, record);
  }
  function maskDynamic() {
    for (const [node, saved] of masked) {
      if (!node.isConnected || signedIn()) {
        if (!saved.hidden) node.removeAttribute('hidden');
        if (!saved.inert) node.removeAttribute('inert');
        node.removeAttribute('data-cc-gate-masked');
        saved.button.remove(); masked.delete(node);
      }
    }
    if (signedIn() || !document.body) return;
    for (const node of document.querySelectorAll(DYNAMIC)) {
      if (masked.has(node) || node.parentElement?.closest(DYNAMIC)) continue;
      if (node.closest('[hidden], [inert]') || node.getAttribute('aria-hidden') === 'true') continue;
      if (!document.getElementById('cc-account-gate-style')) {
        const style = document.createElement('style');
        style.id = 'cc-account-gate-style';
        style.textContent = '[data-cc-gate-masked]{display:none!important}';
        (document.head || root).appendChild(style);
      }
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button cc-account-gate-buy';
      button.textContent = t(node.matches('shopify-accelerated-checkout-cart') ? 'checkout' : 'buy');
      button.dataset.ccAccountRequired = '';
      masked.set(node, { hidden: node.hasAttribute('hidden'), inert: node.hasAttribute('inert'), button });
      replacements.set(button, node);
      node.setAttribute('hidden', ''); node.setAttribute('inert', ''); node.setAttribute('data-cc-gate-masked', '');
      node.insertAdjacentElement('afterend', button);
    }
  }
  function refresh() { maskDynamic(); preparePending(); }
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => { scheduled = false; refresh(); });
  }
  // No customer ID, cart token, item data, or credentials in public events or this API.
  window.CardcAccountGate = Object.freeze({ version: '1.1.0', requireAccount, resumePending, refresh });
  window.addEventListener('pageshow', refresh);
  window.addEventListener('pagehide', () => {
    document.getElementById(NOTICE)?.remove(); noticeKey = '';
  });
  new MutationObserver(schedule).observe(root, { attributes: true, attributeFilter: ['data-cc-profile-complete'] });
  new MutationObserver(schedule).observe(root, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();
})();

